import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Droplets, Feather, Move3d, Wind } from "lucide-react";
import { FadeUp, ImageReveal, MaskedLines } from "@/components/Reveal";
import { HeroParticles } from "@/components/HeroParticles";
import { HeroHeadline } from "@/components/HeroHeadline";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { Newsletter } from "@/components/Newsletter";
import { COLLECTIONS, formatPrice } from "@/data/products";
import { useCatalog } from "@/data/catalog";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[12%] h-[124%]">
        <motion.div
          className="w-full h-full"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <img
            src="/assets/brand/hero-v2-logo.png"
            alt="MVM athlete mid battle-rope slam in a dark chalkboard gym"
            className="hero-drift w-full h-full object-cover object-[65%_center]"
          />
        </motion.div>
        <div className="hero-mist hero-mist-a" />
        <div className="hero-mist hero-mist-b" />
        <div className="hero-sweep" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-obsidian/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-transparent to-transparent" />
      <HeroParticles />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pt-32 pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-mist text-xs md:text-sm tracking-[0.35em] uppercase mb-6"
        >
          MVM Athletics — Est. on discipline
        </motion.p>
        <HeroHeadline delay={0.5} />
        <MaskedLines
          lines={["BEYOND YOUR LIMITS."]}
          delay={0.85}
          as="h2"
          className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl text-stone mt-2"
        />
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-6 max-w-md text-stone/70 leading-relaxed text-sm md:text-base"
        >
          The only competition that matters. Premium performance apparel engineered for movement, training and
          everyday life.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/shop"
            data-testid="hero-cta-shop"
            className="btn-slide bg-stone text-obsidian font-display uppercase tracking-[0.2em] text-sm px-10 py-4 hover:bg-mist transition-colors"
          >
            <span className="btn-label">Shop Collection</span>
            <span className="btn-label-dup">Shop Collection</span>
          </Link>
          <a
            href="#story"
            data-testid="hero-cta-brand"
            className="btn-slide border border-stone/40 text-stone font-display uppercase tracking-[0.2em] text-sm px-10 py-4 hover:border-mist hover:text-mist transition-colors"
          >
            <span className="btn-label">Explore the Brand</span>
            <span className="btn-label-dup">Explore the Brand</span>
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 right-8 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-stone/50"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase [writing-mode:vertical-lr]">Scroll</span>
        <motion.span
          className="w-px h-14 bg-stone/40 origin-top"
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

const Collections = () => (
  <section id="collections" data-testid="collections-section" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
    <FadeUp>
      <div className="flex items-end justify-between mb-14 md:mb-20">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">01 — The Range</p>
          <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight">Featured Collections</h2>
        </div>
        <Link to="/shop" data-testid="collections-view-all" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase link-sweep text-stone/70 hover:text-stone">
          View all <ArrowUpRight size={16} />
        </Link>
      </div>
    </FadeUp>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 auto-rows-[300px] sm:auto-rows-[240px] md:auto-rows-[300px]">
      {COLLECTIONS.map((c, i) => (
        <FadeUp key={c.id} delay={i * 0.08} className={i === 0 ? "sm:col-span-2 sm:row-span-2" : ""}>
          <Link
            to={`/shop?cat=${c.id}`}
            data-testid={`collection-card-${c.id}`}
            className="group relative block w-full h-full overflow-hidden bg-deep"
          >
            <img
              src={c.image}
              alt={`MVM ${c.label} collection`}
              loading="lazy"
              style={c.pos ? { objectPosition: c.pos } : undefined}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out-expo group-hover:scale-[1.07]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/10 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-5 md:p-7">
              <p className="text-[11px] tracking-[0.3em] uppercase text-mist mb-1 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                {c.line}
              </p>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl md:text-3xl uppercase font-medium tracking-wide">{c.label}</h3>
                <ArrowUpRight className="text-mist opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500" size={22} />
              </div>
            </div>
          </Link>
        </FadeUp>
      ))}
    </div>
  </section>
);

const ProductRow = ({ id, index, title, eyebrow, products, testId }) => (
  <section id={id} data-testid={testId} className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
    <FadeUp>
      <div className="flex items-end justify-between mb-12 md:mb-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">{index} — {eyebrow}</p>
          <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight">{title}</h2>
        </div>
        <Link to="/shop" data-testid={`${testId}-view-all`} className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase link-sweep text-stone/70 hover:text-stone">
          Shop all <ArrowUpRight size={16} />
        </Link>
      </div>
    </FadeUp>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
      {products.map((p, i) => (
        <FadeUp key={p.id} delay={i * 0.07}>
          <ProductCard product={p} index={i} />
        </FadeUp>
      ))}
    </div>
  </section>
);

const CHAPTERS = [
  {
    n: "01",
    title: "Discipline",
    quote: "The hardest battle is the one in your head.",
    body: "MVM exists for the ones who show up when no one is watching. Every stitch is a reminder: the work you do with yourself is the work that counts.",
    image: "/assets/brand/image3.png",
  },
  {
    n: "02",
    title: "Resilience",
    quote: "Push past the wall. Become who you're capable of.",
    body: "Marathon training, the 5am lift, the run club in the rain. Apparel built to go as far as you do — and then one rep further.",
    image: "/assets/brand/image29.png",
  },
  {
    n: "03",
    title: "Execution",
    quote: "No excuses. Just execution.",
    body: "Me today, better than me yesterday. That's the whole philosophy. Premium materials, honest construction, zero noise.",
    image: "/assets/brand/image27.png",
  },
];

const Story = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(CHAPTERS.length - 1, Math.floor(v * CHAPTERS.length)));
  });

  const jump = (i) => {
    const el = document.querySelector(`[data-testid="story-chapter-0${i + 1}"]`);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -120 });
    else el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section id="story" ref={ref} data-testid="story-section" className="relative bg-abyss border-y border-stone/10">
      {/* Mobile: slim fill line pinned under the header */}
      <div data-testid="story-progress-mobile" className="md:hidden sticky top-0 z-20 h-[3px] bg-stone/10">
        <motion.div style={{ scaleX: scrollYProgress }} className="h-full w-full bg-mist origin-left" />
      </div>

      {/* Desktop: 01–03 rail with fill line */}
      <div data-testid="story-progress" className="hidden xl:block absolute left-8 2xl:left-12 top-0 bottom-0 z-20">
        <div className="sticky top-[32vh]">
          <div className="relative flex flex-col items-center gap-14">
            <span className="absolute top-3 bottom-3 w-px bg-stone/15" aria-hidden="true" />
            <motion.span
              style={{ scaleY: scrollYProgress }}
              className="absolute top-3 bottom-3 w-px bg-mist origin-top"
              aria-hidden="true"
            />
            {CHAPTERS.map((ch, i) => (
              <button
                key={ch.n}
                data-testid={`story-progress-${ch.n}`}
                onClick={() => jump(i)}
                aria-label={`Jump to chapter ${ch.n} — ${ch.title}`}
                className={`relative z-10 bg-abyss px-1.5 py-1 font-display text-sm tracking-[0.2em] transition-colors duration-500 ${
                  active === i ? "text-mist" : "text-stone/30 hover:text-stone/60"
                }`}
              >
                {ch.n}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:pl-28 py-24 md:py-36">
      <FadeUp>
        <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">03 — The Manifesto</p>
        <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight max-w-3xl leading-[1.05]">
          Discipline today.<br />Strength forever.
        </h2>
      </FadeUp>
      <FadeUp delay={0.1}>
        <div data-testid="philosophy-block" className="mt-14 md:mt-20 max-w-4xl">
          <p className="text-xs tracking-[0.3em] uppercase text-stone/40 mb-6">Our Philosophy</p>
          <p className="text-mist text-lg md:text-2xl leading-snug font-medium max-w-3xl">
            At MVM, our sportswear brand embodies a philosophy forged in the crucible of personal triumph. Founded
            by a former athlete who scaled the peaks of their sports career, MVM stands as a testament to the
            relentless pursuit of excellence.
          </p>
          <div className="mt-8 md:columns-2 md:gap-12 space-y-5 text-stone/60 leading-relaxed">
            <p>
              Our ethos is rooted in the values that propelled our founder to greatness: hard work, unwavering
              attention to detail, unyielding discipline, and the sheer determination to find a way to win against
              all odds. MVM is not just a brand; it's a mindset — a commitment to challenging oneself and overcoming
              barriers.
            </p>
            <p>
              Every stitch in our apparel carries the spirit of resilience, every seam a symbol of tenacity. We
              believe in pushing boundaries, embracing challenges, and constantly striving for self-improvement. MVM
              is a declaration that the true competition lies within — a battle against one's own limitations.
            </p>
            <p>
              Our founder's journey, marked by triumphs and setbacks, is etched into the fabric of MVM. Each product
              is a fusion of functionality and style, reflecting our commitment to empowering athletes to exceed
              their own expectations.
            </p>
            <p>
              Join us in the pursuit of greatness, where the mantra is simple but profound: Me vs Me. In this arena,
              victory is not just about conquering opponents; it's about conquering oneself.
            </p>
          </div>
          <p className="mt-10 font-display uppercase tracking-wide text-stone text-lg md:text-xl leading-snug max-w-2xl">
            MVM — where hard work, attention to detail, discipline and overcoming all odds converge to create a
            winning narrative.
          </p>
        </div>
      </FadeUp>
      <div className="mt-16 md:mt-24 space-y-20 md:space-y-32">
        {CHAPTERS.map((ch, i) => (
          <div
            key={ch.n}
            data-testid={`story-chapter-${ch.n}`}
            className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
          >
            <ImageReveal
              src={ch.image}
              alt={`MVM manifesto — ${ch.title}`}
              className="aspect-[4/5] max-h-[560px] w-full bg-deep"
              imgClassName="w-full h-full object-contain"
            />
            <FadeUp delay={0.15}>
              <span className="font-display text-outline text-7xl md:text-8xl font-bold leading-none">{ch.n}</span>
              <h3 className="font-display text-3xl md:text-5xl uppercase font-semibold tracking-tight mt-4">{ch.title}</h3>
              <p className="text-mist text-lg md:text-xl mt-5 font-medium leading-snug">“{ch.quote}”</p>
              <p className="text-stone/60 mt-5 leading-relaxed max-w-md">{ch.body}</p>
            </FadeUp>
          </div>
        ))}
      </div>
    </div>
    </section>
  );
};

const FeaturedProduct = () => {
  const { getProduct } = useCatalog();
  const product = getProduct("onyx-motion-tee");
  if (!product) return null;
  return (
    <section data-testid="featured-product-section" className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <div className="grid lg:grid-cols-5 gap-10 lg:gap-20 items-center">
        <div className="lg:col-span-3 relative">
          <ImageReveal
            src={product.images[0]}
            alt={product.name}
            className="aspect-[4/5] lg:aspect-[5/5]"
            imgClassName="w-full h-full object-cover"
          />
          <span aria-hidden="true" className="absolute -top-8 -left-2 font-display text-outline text-8xl md:text-9xl font-bold uppercase pointer-events-none select-none hidden lg:block">
            Onyx
          </span>
        </div>
        <FadeUp delay={0.2} className="lg:col-span-2">
          <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">04 — Featured Drop</p>
          <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight leading-[1.02]">
            {product.name}
          </h2>
          <p className="text-stone/60 mt-6 leading-relaxed max-w-md">{product.description}</p>
          <p className="font-display text-2xl text-mist mt-6">{formatPrice(product.price)}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to={`/product/${product.id}`}
              data-testid="featured-product-cta"
              className="btn-slide bg-stone text-obsidian font-display uppercase tracking-[0.2em] text-sm px-10 py-4 hover:bg-mist transition-colors"
            >
              <span className="btn-label">Shop Now</span>
              <span className="btn-label-dup">Shop Now</span>
            </Link>
            <Link
              to="/shop?tag=new"
              data-testid="featured-drop-cta"
              className="btn-slide border border-stone/40 font-display uppercase tracking-[0.2em] text-sm px-10 py-4 hover:border-mist hover:text-mist transition-colors"
            >
              <span className="btn-label">All New Arrivals</span>
              <span className="btn-label-dup">All New Arrivals</span>
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const TECH = [
  { icon: Wind, title: "Breathable Knits", body: "Open-engineered structures that move heat out when the work rate climbs.", image: "/assets/brand/image12.png" },
  { icon: Droplets, title: "Moisture Management", body: "Sweat-wicking yarns pull moisture off the skin to keep you dry through the session.", image: "/assets/brand/image10.png" },
  { icon: Move3d, title: "Four-Way Stretch", body: "Materials that move in every direction you do — squats, strides and stretches.", image: "/assets/brand/image9.png" },
  { icon: Feather, title: "Lightweight Durability", body: "Feather-light construction with reinforced seams, built to outlast the program.", image: "/assets/brand/image5.png" },
];

const Tech = () => (
  <section data-testid="tech-section" className="bg-abyss border-y border-stone/10">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <FadeUp>
        <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">05 — Fabric Technology</p>
        <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight max-w-3xl leading-[1.05]">
          Engineered for motion
        </h2>
      </FadeUp>
      <div className="mt-14 md:mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        {TECH.map((t, i) => (
          <FadeUp key={t.title} delay={i * 0.08}>
            <div data-testid={`tech-card-${i}`} className="group relative overflow-hidden border border-stone/10 bg-deep h-full">
              <div className="relative h-44 overflow-hidden">
                <img
                  src={t.image}
                  alt={t.title}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-70 transition-transform duration-[1.2s] ease-out-expo group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep to-transparent" />
              </div>
              <div className="p-6">
                <t.icon size={22} strokeWidth={1.4} className="text-mist mb-4" />
                <h3 className="font-display uppercase tracking-wide text-lg">{t.title}</h3>
                <p className="text-sm text-stone/55 mt-3 leading-relaxed">{t.body}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const LOOKS = [
  { image: "/assets/products/onyx-d.webp", product: "onyx-motion-tee", label: "Onyx Motion Tee" },
  { image: "/assets/products/pullover-a.webp", product: "signature-pullover", label: "Signature Pullover" },
  { image: "/assets/products/ls-d.webp", product: "athletic-long-sleeve", label: "Athletic Long Sleeve" },
  { image: "/assets/products/stripe-a.webp", product: "signature-stripe-sweatpants", label: "Stripe Sweatpants" },
  { image: "/assets/products/oshorts-c.webp", product: "onyx-motion-shorts", label: "Onyx Motion Shorts" },
  { image: "/assets/products/beanie-a.webp", product: "grey-beanie", label: "Grey Beanie" },
];

const Lookbook = () => (
  <section data-testid="lookbook-section" className="py-24 md:py-36 overflow-hidden">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <FadeUp>
        <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">06 — Lookbook</p>
        <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight">Worn in the work</h2>
      </FadeUp>
    </div>
    <FadeUp delay={0.15}>
      <div data-testid="lookbook-rail" className="mt-14 flex gap-4 md:gap-6 overflow-x-auto px-6 md:px-12 pb-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {LOOKS.map((look) => (
          <Link
            key={look.label}
            to={`/product/${look.product}`}
            data-testid={`lookbook-${look.product}`}
            className="group relative shrink-0 w-[70vw] sm:w-[42vw] lg:w-[26vw] aspect-[3/4] overflow-hidden snap-start bg-deep"
          >
            <img
              src={look.image}
              alt={`MVM lookbook — ${look.label}`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.3s] ease-out-expo group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
              <span className="font-display uppercase tracking-widest text-sm">{look.label}</span>
              <span className="text-xs tracking-[0.2em] uppercase text-mist inline-flex items-center gap-1">
                Shop look <ArrowRight size={13} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </FadeUp>
  </section>
);

const Community = () => (
  <section id="community" data-testid="community-section" className="relative border-t border-stone/10 overflow-hidden">
    <div className="grid lg:grid-cols-2">
      <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
        <img src="/assets/community/group-training.jpg" alt="MVM community training together at a pop-up event" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-obsidian/30" />
      </div>
      <div className="flex flex-col justify-center px-6 md:px-16 py-20 lg:py-0 bg-abyss">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">07 — Community</p>
          <h2 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight leading-[1.02]">
            Me today.<br />Better than<br />me yesterday.
          </h2>
          <p className="text-stone/60 mt-6 leading-relaxed max-w-md">
            Run clubs before sunrise, lifts after dark, Pilates before work. MVM is worn by people becoming better than
            they were yesterday. Share your session, your look, your progress.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-6">
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              data-testid="community-follow-cta"
              className="btn-slide bg-stone text-obsidian font-display uppercase tracking-[0.2em] text-sm px-10 py-4 hover:bg-mist transition-colors"
            >
              <span className="btn-label">Follow @MVM</span>
              <span className="btn-label-dup">Follow @MVM</span>
            </a>
            <span className="font-display uppercase tracking-[0.25em] text-mist text-sm">#MVM</span>
            <Link
              to="/community"
              data-testid="community-wall-link"
              className="font-display uppercase tracking-[0.2em] text-sm text-stone/70 hover:text-mist transition-colors link-sweep"
            >
              See the wall
            </Link>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const NewsletterSection = () => (
  <section data-testid="newsletter-section" className="relative py-28 md:py-40 grain overflow-hidden">
    <div className="absolute inset-0">
      <img src="/assets/brand/image25.png" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/60 to-obsidian" />
    </div>
    <div className="relative z-10 max-w-3xl mx-auto px-6 text-center flex flex-col items-center">
      <FadeUp>
        <h2 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tight">Stay in motion.</h2>
        <p className="text-stone/60 mt-6 max-w-md mx-auto leading-relaxed">
          Be the first to know about new collections, product drops and exclusive releases.
        </p>
        <div className="mt-10 flex justify-center w-full">
          <Newsletter />
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  const { getProduct, newArrivals } = useCatalog();
  return (
    <main>
      <Hero />
      <Marquee />
      <Collections />
      <ProductRow id="new" index="02" eyebrow="Just Dropped" title="New Arrivals" products={newArrivals.slice(0, 8)} testId="new-arrivals-section" />
      <Story />
      <FeaturedProduct />
      <Tech />
      <Lookbook />
      <ProductRow
        id="best"
        index="08"
        eyebrow="The Signature Capsule"
        title="Best Sellers"
        products={["onyx-motion-tee", "signature-pullover", "athletic-long-sleeve", "signature-stripe-sweatpants"].map(getProduct).filter(Boolean)}
        testId="best-sellers-section"
      />
      <Community />
      <NewsletterSection />
    </main>
  );
}
